import * as S from './styles';
import { useMultiTickerStore } from '@/app/hooks/MultiTickerStore';
import { ThemeContext } from '@/theme/ThemeProvider';
import { useFavourite } from '@app/hooks';
import { goTo } from '@app/hooks/navigation';
import { useDemoTradingStore } from '@app/hooks/trade/demoTradingStore';
import { useExchangeStore } from '@app/hooks/trade/exchangeStore.tsx';
import { useIndexPrice } from '@app/hooks/trade/indexprice';
import useTradeStore from '@app/hooks/trade/tradeStore';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconGripVertical } from '@tabler/icons-react';
import BigNumber from 'bignumber.js';
import throttle from 'lodash/throttle';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const FavListItem = ({ item, isDragging: globalIsDragging }) => {
  const { mutate } = useTradeStore();
  const navigation = useNavigate();
  const { textsilver } = useContext(ThemeContext).theme.COLORS;
  const { removeFavourite, isLoading } = useFavourite();
  const { EPs } = useExchangeStore();
  const { isDemoMode } = useDemoTradingStore();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item?.dragId || item?.id || item?.ep_id,
  });

  const dndStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const targetId = item?.ep_id || item?.id;
  const { getPrice } = useIndexPrice(targetId);
  const { tickers } = useMultiTickerStore();
  const symbol = item?.pairs?.replace('/', '').toLowerCase();
  const realTimeData = tickers[symbol];
  const info24H = { rate: realTimeData?.change };

  const EP = EPs?.find?.((e) => (e?.ep_id || e?.id) === targetId);

  const [throttledPrice, setThrottledPrice] = useState(getPrice(targetId) || 0);

  const throttledUpdatePrice = useCallback(
    throttle((newPrice) => {
      setThrottledPrice(newPrice);
    }, 300),
    []
  );

  useEffect(() => {
    throttledUpdatePrice(getPrice(targetId) || 0);
  }, [getPrice(targetId), throttledUpdatePrice, targetId]);

  const handleClickListItem = useCallback(() => {
    if (isDragging || globalIsDragging) {
      return;
    }

    const targetId = item?.ep_id || item?.id;

    if (!targetId) {
      console.error('targetId가 없습니다:', { item });
      return;
    }

    if (!EPs || EPs.length === 0) {
      console.error('EPs 데이터가 없습니다:', { EPs });
      return;
    }

    const selected = EPs?.find((ep) => {
      const epId = ep.ep_id || ep.id;
      return epId === targetId;
    });

    if (selected) {
      const navigateId = selected.ep_id || selected.id;
      const basePath = isDemoMode ? '/demo-trade' : '/trade';

      mutate({
        ep_id: navigateId,
        ...selected,
      });

      goTo(navigation, `${basePath}/${navigateId}`);
    } else {
      console.error('매칭되는 EP를 찾을 수 없습니다.', {
        targetId,
        EPsLength: EPs.length,
      });
    }
  }, [
    EPs,
    mutate,
    navigation,
    item?.id,
    item?.ep_id,
    isDragging,
    globalIsDragging,
    isDemoMode,
  ]);

  const handleRemoveClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isLoading || isDragging || globalIsDragging) {
      return;
    }

    try {
      await removeFavourite(item);
    } catch (error) {
      console.error('removeFavourite 에러:', error);
    }
  };

  const isDisabled = isLoading || isDragging || globalIsDragging;
  const changeRate = BigNumber(info24H?.rate).toFormat(2);
  const isPositive = info24H?.rate > 0;

  return (
    <S.Container
      ref={setNodeRef}
      style={dndStyle}
      $isDragging={isDragging}
      $textsilver={textsilver}
      {...attributes}
    >
      <S.StarIconWrapper
        viewBox="0 0 14 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        $disabled={isDisabled}
        onClick={handleRemoveClick}
      >
        <g clipPath="url(#clip0_2272_7713)">
          <path
            d="M13.9634 5.36955C13.8712 5.08606 13.6198 4.88536 13.3235 4.85855L9.28208 4.49165L7.68491 0.752327C7.56698 0.477603 7.29867 0.300293 7.00002 0.300293C6.70137 0.300293 6.43295 0.477603 6.31577 0.752327L4.71859 4.49165L0.676564 4.85855C0.380264 4.88589 0.129359 5.0866 0.0366454 5.36955C-0.0555345 5.65303 0.0295957 5.96396 0.25369 6.1605L3.30866 8.83927L2.4079 12.8065C2.342 13.0982 2.45522 13.3999 2.69726 13.5748C2.82736 13.6694 2.98021 13.7166 3.13359 13.7166C3.2654 13.7166 3.39731 13.6815 3.51513 13.611L7.00002 11.5273L10.4843 13.611C10.7399 13.7638 11.0613 13.7498 11.3028 13.5748C11.5448 13.3999 11.658 13.0982 11.5921 12.8065L10.6914 8.83927L13.7463 6.1605C13.9703 5.96396 14.0556 5.65367 13.9634 5.36955Z"
            fill="#FFCC0D"
          />
        </g>
        <defs>
          <clipPath id="clip0_2272_7713">
            <rect width="14" height="14" fill="white" />
          </clipPath>
        </defs>
      </S.StarIconWrapper>

      <S.InfoContainer onClick={handleClickListItem}>
        <S.PairsWrapper>
          <S.PairsText>{item?.pairs}</S.PairsText>
        </S.PairsWrapper>
      </S.InfoContainer>

      <S.PriceContainer onClick={handleClickListItem}>
        <S.PriceText>
          {BigNumber(throttledPrice).toFormat(EP?.dp || 2)}
        </S.PriceText>
        <S.ChangeText $isPositive={isPositive}>
          {changeRate === 'NaN' ? '-' : `${changeRate}%`}
        </S.ChangeText>
      </S.PriceContainer>

      <S.DragHandle {...listeners} $textsilver={textsilver}>
        <IconGripVertical size={14} />
      </S.DragHandle>
    </S.Container>
  );
};

export default FavListItem;
